// Helpers shared across the Settings sections.
//
// These were inline in the old single-file Settings page. They are pure and
// used by more than one section now (cameras, bulk add, recorders), so they
// live here rather than being duplicated or re-exported from a component file.

import type { LucideIcon } from "lucide-react";

import type { Role } from "../../api";
import { InfoHint } from "../../components/InfoHint";
import { friendlyError } from "../../errors";

/** Collapse backend apply-errors into role-appropriate copy. */
export function humanizeApplyErrors(errs: string[], role: Role): string {
  if (role === "dev") return errs.join("; ");
  return [...new Set(errs.map((e) => friendlyError(e, role)))].join(" ");
}

/**
 * A camera's stable id, derived from its source rather than typed:
 *   direct RTSP -> the last path segment (".../cam01" -> "cam01")
 *   NVR channel -> "<nvr_id>-ch<channel>"
 */
export function deriveCameraId(cam: {
  nvr_id?: string | null;
  channel?: number | null;
  rtsp_url?: string;
}): string {
  if (cam.nvr_id) return `${cam.nvr_id}-ch${cam.channel ?? ""}`;
  const base = (cam.rtsp_url || "").split("?")[0].replace(/\/+$/, "");
  const tail = base.split("/").pop() || "";
  return tail || base;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Split "rtsp://user:pass@host/path" into a credential-free base plus the
 * decoded username/password. Typing credentials straight into the URL breaks
 * as soon as the password contains a URL-reserved character (`@ : / ? #`) —
 * the `@` splits the authority in the wrong place and the camera answers 401.
 */
export function splitRtspCredentials(url: string): {
  base: string;
  username: string;
  password: string;
} {
  const m = (url || "").match(/^([a-zA-Z][\w+.-]*:\/\/)([^/@]*)@(.*)$/);
  if (!m) return { base: url || "", username: "", password: "" };
  const [, scheme, userinfo, rest] = m;
  const i = userinfo.indexOf(":");
  return {
    base: `${scheme}${rest}`,
    username: safeDecode(i === -1 ? userinfo : userinfo.slice(0, i)),
    password: i === -1 ? "" : safeDecode(userinfo.slice(i + 1)),
  };
}

/** Re-attach credentials to a base URL, percent-encoding both halves. */
export function applyRtspCredentials(
  base: string,
  username: string,
  password: string,
): string {
  const clean = splitRtspCredentials(base).base.trim();
  if (!username && !password) return clean;
  const m = clean.match(/^([a-zA-Z][\w+.-]*:\/\/)(.*)$/);
  if (!m) return clean;
  const [, scheme, rest] = m;
  const cred = password
    ? `${encodeURIComponent(username)}:${encodeURIComponent(password)}`
    : encodeURIComponent(username);
  return `${scheme}${cred}@${rest}`;
}

/**
 * Expand a "first camera" URL into `count` URLs by incrementing the trailing
 * number in its last path segment, preserving zero-padding.
 */
export function buildBulkUrls(firstUrl: string, count: number): string[] {
  const [base, query] = firstUrl.trim().split("?");
  const segs = base.split("/");
  const last = segs.pop() ?? "";
  const m = last.match(/^(\D*?)(\d+)(\D*)$/);
  if (!m) return [];
  const [, pre, digits, post] = m;
  const start = parseInt(digits, 10);
  const width = digits.length;
  const prefix = segs.join("/");
  const urls: string[] = [];
  for (let i = 0; i < count; i++) {
    const n = String(start + i).padStart(width, "0");
    urls.push(`${prefix}/${pre}${n}${post}${query ? `?${query}` : ""}`);
  }
  return urls;
}

export const BRAND_OPTIONS = [
  { id: "hikvision", label: "Hikvision" },
  { id: "dahua", label: "Dahua" },
  { id: "axis", label: "Axis" },
  { id: "uniview", label: "Uniview (UNV)" },
  { id: "generic", label: "Generic / custom" },
];

/** Brands whose sub-stream URL shape Blinkfree knows (mirrors the api map). */
export const BRANDS_WITH_SUB_STREAM = new Set(["hikvision", "dahua", "uniview"]);

/**
 * Selectable source-resolution ceilings. Mirrors MAX_HEIGHT_CHOICES in
 * `routes/cameras.py`; the backend is what enforces them.
 */
export const MAX_HEIGHT_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "No limit" },
  { value: 480, label: "480p (SD)" },
  { value: 720, label: "720p (HD)" },
  { value: 1080, label: "1080p (Full HD)" },
  { value: 1440, label: "1440p (2K)" },
  { value: 2160, label: "2160p (4K)" },
];

/** Analysis frame-rate ceilings per camera (0 = the system default). */
export const INGEST_FPS_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "System default" },
  { value: 5, label: "5 fps" },
  { value: 10, label: "10 fps" },
  { value: 15, label: "15 fps" },
  { value: 20, label: "20 fps" },
  { value: 25, label: "25 fps" },
  { value: 30, label: "30 fps" },
  { value: 60, label: "60 fps (every frame on most cameras)" },
];

export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** i;
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Section shell: a titled block with a one-line description and actions.
 *
 *  `description` stays to a single sentence; anything longer belongs in the
 *  `hint` question mark next to the title, so the page reads as a list of
 *  things you can do rather than a manual. */
export function SettingsSection({
  title,
  description,
  hint,
  actions,
  children,
}: {
  title: string;
  description?: string;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-1.5 text-lg font-semibold text-white">
            {title}
            {hint && <InfoHint side="bottom">{hint}</InfoHint>}
          </h2>
          {description && (
            <p className="mt-0.5 max-w-2xl text-sm text-bf-muted">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Consistent empty state: says what is missing and what to do about it. */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="bf-card flex flex-col items-center gap-2 py-10 text-center">
      {Icon && <Icon size={28} className="text-bf-muted/60" />}
      <p className="text-sm font-medium text-bf-text">{title}</p>
      {hint && <p className="max-w-md text-sm text-bf-muted">{hint}</p>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

/** Inline status note. One component so every section reads the same. */
export function Note({
  tone,
  children,
}: {
  tone: "info" | "success" | "warning" | "error";
  children: React.ReactNode;
}) {
  const styles = {
    info: "border-white/10 bg-white/5 text-bf-muted",
    success: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
    warning: "border-amber-500/30 bg-amber-500/5 text-amber-200",
    error: "border-red-500/30 bg-red-500/5 text-red-300",
  }[tone];
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${styles}`}>{children}</div>
  );
}
